<template>
  <div v-if="isLoadingAuth || isLoading" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
  </div>

  <!-- Not logged in -->
  <div v-else-if="!user" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <div class="text-red-300 text-sm p-4 bg-gray-800 border border-gray-700 rounded-md">กรุณาเข้าสู่ระบบ</div>
  </div>

  <!-- Error full-screen (rare) -->
  <div v-else-if="error" class="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-70">
    <div class="text-red-300 text-sm p-4 bg-gray-800 border border-gray-700 rounded-md">{{ error }}</div>
  </div>

  <!-- Main Modal -->
  <div v-else class="fixed inset-0 bg-gray-900 bg-opacity-70 backdrop-blur-sm flex items-center justify-center p-4 z-50 transition-opacity duration-300 animate-modalFade">
    <div class="relative w-full max-w-lg sm:max-w-2xl bg-gray-800 text-gray-300 rounded-lg p-4 sm:p-6 flex flex-col gap-4 shadow-lg border border-gray-700 animate-scaleIn">

      <!-- Error & Success (inline) -->
      <div v-if="error" class="p-2 bg-red-900/60 text-red-300 text-center text-sm rounded-md border border-red-800">
        {{ error }}
      </div>
      <div v-if="successMessage" class="p-2 bg-green-900/60 text-green-300 text-center text-sm rounded-md border border-green-800">
        {{ successMessage }}
      </div>

      <!-- Header -->
      <div class="flex justify-between items-start sm:items-center border-b border-gray-700 pb-3">
        <div class="flex items-start sm:items-center gap-3">
          <i class="fas fa-coins text-gray-100 text-2xl"></i>
          <div class="space-y-0.5">
            <h1 class="text-lg font-semibold text-indigo-300">ขายไอเทม</h1>
            <p class="text-indigo-300 text-xs">เลือกไอเทมที่ต้องการขาย</p>
            <p class="text-indigo-300 text-xs">จำนวนไอเทมที่เลือก: <strong>{{ selectedItems.length }}</strong></p>
          </div>
        </div>
        <div class="flex items-center gap-2">
          <select
            v-model="sortPreference"
            @change="saveSortPreference"
            class="bg-gray-800 text-indigo-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-700 border border-gray-600"
          >
            <option value="type">ประเภท</option>
            <option value="inventory">ล่าสุด</option>
          </select>
          <button @click="closeModal" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200" aria-label="ปิด">
            <i class="fas fa-times text-base"></i>
          </button>
        </div>
      </div>

      <!-- Item List -->
      <div class="max-h-96 overflow-y-auto p-2" id="style-1">
        <div v-if="isLoading" class="text-center text-indigo-200 py-4 text-sm">กำลังโหลดไอเทม...</div>
        <div v-else-if="sellableItems.length === 0" class="text-center text-indigo-200 py-4 text-sm">
          ไม่พบไอเทมที่สามารถขายได้
        </div>

        <transition-group
          v-else
          name="item"
          tag="div"
          class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1.5 items-center"
        >
          <div
            v-for="item in sortedSellableItems"
            :key="item.id"
            @click="toggleItemSelection(item)"
            :class="[
              'cursor-pointer rounded-md border transition-all duration-200 relative p-1.5',
              selectedItems.includes(item.id)
                ? 'bg-indigo-700/50 border-indigo-600 ring-1 ring-indigo-700'
                : 'bg-gray-800 border-gray-700 hover:bg-gray-700 hover:border-indigo-600'
            ]"
            role="button"
            :aria-label="`เลือกไอเทม ${item.name}`"
          >
            <div class="flex items-center gap-2">
              <img
                :src="item.img || 'default-item.png'"
                :alt="item.name"
                class="w-9 h-9 rounded-md object-cover border-2"
                :class="[
                  item.rarityColors?.borderColor || 'border-gray-600',
                  item.rarityColors?.bgColor || 'bg-gray-800'
                ]"
                @error="handleImageError"
              />
              <div class="flex-1 min-w-0">
                <h3 class="text-xs sm:text-sm font-semibold text-indigo-300 truncate">{{ item.name }}</h3>
                <p class="text-xs text-indigo-100 truncate">ประเภท: {{ item.type }}</p>
                <p class="text-xs text-indigo-100">
                  ราคา: {{ item.price?.toLocaleString() || 0 }}
                  <i :class="getCurrencyIcon(item.currencyType) + ' text-[10px] ml-0.5'"></i>
                </p>
              </div>

              <input
                type="checkbox"
                :checked="selectedItems.includes(item.id)"
                class="absolute top-2 right-2 h-4 w-4 text-indigo-600 focus:ring-indigo-700 border-gray-600 rounded bg-gray-800"
                @click.stop="toggleItemSelection(item)"
                :aria-label="`เลือก ${item.name}`"
              />
            </div>
          </div>
        </transition-group>
      </div>

      <!-- Back to Top -->
      <button
        v-if="showBackToTop"
        @click="scrollToTop"
        class="fixed bottom-20 right-6 bg-indigo-600 hover:bg-indigo-700 text-white py-2 px-3 rounded-md transition-colors duration-200 border border-indigo-700"
        aria-label="กลับไปด้านบน"
      >
        <i class="fas fa-arrow-up text-sm"></i>
      </button>

      <!-- Action Bar -->
      <div class="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-2 border-t border-gray-700 pt-3">
        <div class="flex gap-2">
          <button
            v-if="sellableItems.length > 0"
            @click="selectAllItems"
            :disabled="isSelling || sellableItems.length === 0"
            :class="[
              'bg-blue-600 hover:bg-blue-700 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-blue-700',
              { 'opacity-60 cursor-not-allowed': isSelling || sellableItems.length === 0 }
            ]"
          >
            <i class="fas fa-check-square text-xs"></i> เลือกทั้งหมด
          </button>
          <button
            v-if="selectedItems.length > 0"
            @click="deselectAllItems"
            :disabled="isSelling || selectedItems.length === 0"
            :class="[
              'bg-gray-700 hover:bg-gray-600 text-white text-sm py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 border border-gray-600',
              { 'opacity-60 cursor-not-allowed': isSelling || selectedItems.length === 0 }
            ]"
          >
            <i class="fas fa-times-circle text-xs"></i> ยกเลิกเลือกทั้งหมด
          </button>
        </div>

        <div class="flex gap-2">
          <button
            v-if="selectedItems.length > 0"
            @click="confirmSell = true"
            :disabled="isSelling || selectedItems.length === 0"
            :class="[
              'bg-indigo-600 hover:bg-indigo-700 text-white text-sm py-2 px-4 w-full rounded-md text-center transition-colors duration-200 flex items-center justify-center gap-2 border border-indigo-700',
              { 'opacity-60 cursor-not-allowed': isSelling || selectedItems.length === 0 }
            ]"
          >
            <span v-if="!isSelling"><i class="fas fa-coins text-xs"></i> ขาย ({{ selectedItems.length }})</span>
            <div v-else class="animate-spin rounded-full h-4 w-4 border-t-2 border-white"></div>
          </button>

          <button
            @click="closeModal"
            class="flex items-center bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm"
          >
            <i class="fas fa-times mr-1 text-sm"></i> ปิด
          </button>
        </div>
      </div>

      <!-- Confirmation Modal (inner) -->
      <transition name="modal">
        <div
          v-if="confirmSell"
          class="absolute inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center rounded-lg animate-modalFade"
        >
          <div class="bg-gray-800 rounded-lg w-full max-w-md shadow-lg border border-gray-700 animate-scaleIn">
            <div class="p-4 border-b border-gray-700 flex justify-between items-center">
              <h1 class="text-lg font-semibold text-indigo-300">ยืนยันการขาย</h1>
              <button @click="confirmSell = false" class="text-gray-300 hover:text-indigo-300 transition-colors duration-200">
                <i class="fas fa-times text-base"></i>
              </button>
            </div>

            <div class="p-4 space-y-4 max-h-[60vh] overflow-y-auto" id="style-1">
              <section>
                <h2 class="text-xs sm:text-sm font-semibold text-indigo-200 mb-2">รายการไอเทม</h2>
                <div class="space-y-2 max-h-60 overflow-y-auto" id="style-1">
                  <div
                    v-for="item in selectedItemsObjects"
                    :key="item.id"
                    class="bg-gray-800 p-3 rounded-md border border-gray-700"
                  >
                    <div class="flex items-center gap-3">
                      <img
                        :src="item.img || 'default-item.png'"
                        alt="Item Image"
                        class="w-10 h-10 object-cover rounded-md border-2"
                        :class="item.rarityColors?.borderColor || 'border-gray-600'"
                        @error="handleImageError"
                      />
                      <div class="flex-1 min-w-0">
                        <p class="text-sm font-semibold" :class="item.rarityColors?.textColor || 'text-indigo-200'">
                          {{ item.name || 'ไม่ระบุ' }}
                        </p>
                        <p class="text-indigo-300 text-xs truncate">ประเภท: {{ item.type || 'ไม่ระบุ' }}</p>
                        <p class="text-indigo-300 text-xs">
                          ราคาขาย: {{ item.price?.toLocaleString() || 0 }}
                          <i :class="getCurrencyIcon(item.currencyType) + ' text-[10px] ml-0.5'"></i>
                        </p>
                        <p v-if="item.quantity > 1" class="text-indigo-300 text-xs">จำนวน: {{ item.quantity }}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <p class="text-sm font-semibold text-indigo-200 mt-4">
                  ราคารวม:
                  <span v-if="totalPrice.silver > 0">
                    {{ totalPrice.silver.toLocaleString() }} <i class="fa-solid fa-coins text-gray-100 text-[10px]"></i>
                  </span>
                  <span v-if="totalPrice.gold > 0">
                    <span v-if="totalPrice.silver > 0">, </span>{{ totalPrice.gold.toLocaleString() }} <i class="fa-solid fa-g text-yellow-400 text-[10px]"></i>
                  </span>
                  <span v-if="totalPrice.diamond > 0">
                    <span v-if="totalPrice.silver > 0 || totalPrice.gold > 0">, </span>{{ totalPrice.diamond.toLocaleString() }} <i class="fa-solid fa-gem text-blue-400 text-[10px]"></i>
                  </span>
                </p>
              </section>
            </div>

            <div class="p-4 border-t border-gray-700 flex justify-end gap-2">
              <button
                @click="sellItems"
                :disabled="isSelling"
                class="flex items-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-indigo-700 text-sm"
                :class="{ 'opacity-60 cursor-not-allowed': isSelling }"
              >
                <span v-if="!isSelling"><i class="fas fa-coins mr-1 text-sm"></i> ขายทั้งหมด</span>
                <i v-else class="fas fa-spinner animate-spin text-sm"></i>
              </button>
              <button
                @click="confirmSell = false"
                class="flex items-center bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded-md font-semibold transition-all duration-200 border border-gray-600 text-sm"
              >
                <i class="fas fa-times mr-1 text-sm"></i> ปิด
              </button>
            </div>
          </div>
        </div>
      </transition>

      <!-- Selling overlay -->
      <transition name="fade">
        <div
          v-if="isSelling"
          class="absolute inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center rounded-lg"
        >
          <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-indigo-600"></div>
        </div>
      </transition>

    </div>
  </div>
</template>

<script>
import getUser from '@/composables/getUser';
import { projectFunctions } from '@/firebase/config';
import { ref, computed, onMounted } from 'vue';

export default {
  name: 'SellModal',
  props: {
    items: {
      type: Array,
      required: true,
      default: () => [],
    },
    bannedItems: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['close', 'items-sold'],
  setup(props, { emit }) {
    const { user, isLoadingAuth } = getUser();
    const isLoading = ref(false);
    const error = ref(null);
    const successMessage = ref('');
    const selectedItems = ref([]); // Array of item IDs
    const confirmSell = ref(false);
    const isSelling = ref(false);
    const showBackToTop = ref(false);
    const sortPreference = ref('type');

    const defaultSortOrder = [
      'พาหนะ', 'รักษา', 'กัปดัก', 'อาหาร', 'แผนที่','หุ่นยนต์สังหาร', 'ปลดกัปดัก','ระเบิด','ปล้น', 'การ์ด', 'หนังสือ', 'อวัยวะจักรกล', 'วิญญาณ' , 'หมวก', 'แว่นตา', 'เสื้อ', 'เสื้อคลุม', 'เกราะ', 'ปลอกแขน', 'ถุงมือ', 'กางเกง',
      'สนับแข้ง', 'รองเท้า', 'เข็มขัด', 'สร้อยคอ', 'แหวน', 'อาวุธ', 'อาวุธสำรอง',
      'ตะกรุด', 'ผ้ายันต์', 'สายสิญจน์', 'วัสดุวิญญาณ','วัสดุ','กล่อง','ทั่วไป' 
    ];

    const loadSortPreference = () => {
      const savedPreference = localStorage.getItem('inventorySortPreference');
      if (savedPreference && ['type', 'inventory'].includes(savedPreference)) {
        sortPreference.value = savedPreference;
      }
    };

    const saveSortPreference = () => {
      localStorage.setItem('inventorySortPreference', sortPreference.value);
    };

    // Filter sellable items
    const sellableItems = computed(() => {
      return props.items.filter(item => item.type !== 'พาหนะ' && !item.locked && !props.bannedItems.includes(item.name));
    });

    const sortedSellableItems = computed(() => {
      let itemsList = [...sellableItems.value];
      if (sortPreference.value === 'type') {
        const typeOrder = defaultSortOrder.reduce((acc, type, index) => {
          acc[type] = index + 1;
          return acc;
        }, {});
        itemsList.sort((a, b) => {
          const orderA = typeOrder[a.type] || 999;
          const orderB = typeOrder[b.type] || 999;
          if (orderA !== orderB) {
            return orderA - orderB;
          }
          const rarityA = a.rarity ? String(a.rarity) : '';
          const rarityB = b.rarity ? String(b.rarity) : '';
          if (rarityA !== rarityB) {
            return rarityA.localeCompare(rarityB, 'th');
          }
          return a.name.localeCompare(b.name, 'th');
        });
      }
      return itemsList;
    });

    // Get selected items objects
    const selectedItemsObjects = computed(() => {
      return props.items.filter(item => selectedItems.value.includes(item.id));
    });

    // Calculate total price
    const totalPrice = computed(() => {
      return selectedItemsObjects.value.reduce((totals, item) => {
        const currency = item.currencyType || 'silver';
        const price = item.price || 0;
        totals[currency] = (totals[currency] || 0) + price;
        return totals;
      }, { silver: 0, gold: 0, diamond: 0 });
    });

    // Get currency icon class
    const getCurrencyIcon = (currency) => {
      if (currency === 'gold') return 'fa-solid fa-g text-yellow-400';
      if (currency === 'diamond') return 'fa-solid fa-gem text-blue-400';
      return 'fa-solid fa-coins text-gray-100';
    };

    // Handle image error
    const handleImageError = (event) => {
      event.target.src = 'default-item.png';
    };

    // Toggle item selection
    const toggleItemSelection = (item) => {
      if (!item?.id || item.type === 'พาหนะ' || item.locked || props.bannedItems.includes(item.name)) return;
      const index = selectedItems.value.indexOf(item.id);
      if (index === -1) {
        selectedItems.value.push(item.id);
      } else {
        selectedItems.value.splice(index, 1);
      }
      error.value = '';
      successMessage.value = '';
    };

    // Select all items
    const selectAllItems = () => {
      selectedItems.value = sellableItems.value.map(item => item.id);
      error.value = '';
      successMessage.value = '';
    };

    // Deselect all items
    const deselectAllItems = () => {
      selectedItems.value = [];
      error.value = '';
      successMessage.value = '';
    };

    // Sell selected items
    const sellItems = async () => {
      if (!user.value || selectedItems.value.length === 0) {
        error.value = 'กรุณาเลือกไอเทมเพื่อขาย';
        return;
      }
      isSelling.value = true;
      error.value = '';
      try {
        const gameActions = projectFunctions.httpsCallable('gameActions');
        const response = await gameActions({
          action: 'sellItems',
          instanceIds: selectedItems.value,
        });
        console.log('Sell response:', response);
        successMessage.value = `ขาย ${selectedItems.value.length} ไอเทมสำเร็จ!`;
        selectedItems.value = [];
        confirmSell.value = false;
        emit('items-sold');
        setTimeout(() => {
          emit('close');
          successMessage.value = '';
        }, 1000); // Delay closing to show success message
      } catch (err) {
        console.error('Error selling items:', err);
        error.value = err.message || 'เกิดข้อผิดพลาดในการขาย';
      } finally {
        isSelling.value = false;
      }
    };

    // Close modal
    const closeModal = () => {
      selectedItems.value = [];
      error.value = '';
      successMessage.value = '';
      confirmSell.value = false;
      emit('close');
    };

    // Scroll to top
    const scrollToTop = () => {
      const container = document.querySelector('.max-h-96');
      if (container) {
        container.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    // Monitor scroll for back-to-top button
    const handleScroll = () => {
      const container = document.querySelector('.max-h-96');
      if (container) {
        showBackToTop.value = container.scrollTop > 100;
      }
    };

    loadSortPreference();

    onMounted(() => {
      const container = document.querySelector('.max-h-96');
      if (container) {
        container.addEventListener('scroll', handleScroll);
      }
    });

    return {
      user,
      isLoadingAuth,
      isLoading,
      error,
      successMessage,
      selectedItems,
      confirmSell,
      isSelling,
      showBackToTop,
      sellableItems,
      sortedSellableItems,
      selectedItemsObjects,
      totalPrice,
      getCurrencyIcon,
      handleImageError,
      toggleItemSelection,
      selectAllItems,
      deselectAllItems,
      sellItems,
      closeModal,
      scrollToTop,
      sortPreference,
      saveSortPreference,
    };
  },
};
</script>

<style scoped>
.scrollbar-thin::-webkit-scrollbar {
  width: 5px;
}

.scrollbar-thin::-webkit-scrollbar-track {
  background: #2d3748;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb {
  background: #4a5568;
  border-radius: 10px;
}

.scrollbar-thin::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

.scrollbar-thin {
  scrollbar-width: thin;
  scrollbar-color: #4a5568 #2d3748;
}

.animate-spin {
  animation: spin 1s linear infinite;
}

.animate-spin-pulse {
  animation: spin-pulse 1.2s ease-in-out infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes spin-pulse {
  0% { transform: rotate(0deg) scale(1); }
  50% { transform: rotate(180deg) scale(1.2); }
  100% { transform: rotate(360deg) scale(1); }
}

@keyframes modalFade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.animate-modalFade {
  animation: modalFade 0.3s ease-out;
}

@keyframes scaleIn {
  from { transform: scale(0.95); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
}

.animate-scaleIn {
  animation: scaleIn 0.3s ease-out;
}

@keyframes fade {
  from { opacity: 0; }
  to { opacity: 1; }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.item-enter-active,
.item-leave-active {
  transition: all 0.3s ease;
}

.item-enter-from,
.item-leave-to {
  opacity: 0;
  transform: translateY(10px);
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}

input:focus {
  outline: none;
  box-shadow: 0 0 0 2px #6366f1;
}
</style>
